import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api, ApiError, type IntakeItem, type ReferenceData } from '../api/client'
import { invalidateFleet } from '../api/invalidate'
import { SearchSelect, type SearchOption } from '../components/SearchSelect'
import './IntakeQueue.css'

/**
 * The review queue for certificates that arrived by email.
 *
 * This screen exists because OCR is probabilistic and this register is not. The
 * poller reads a mailbox, files the attachment and *proposes* what it thinks the
 * document says; nothing it proposes reaches the register until a person has
 * looked at the scan and agreed. CLAUDE.md's rule — a fabricated insurance date
 * is worse than a blank — is enforced here, by a person, rather than by a
 * confidence threshold.
 *
 * So this is not a list of results to approve in bulk. It is one document at a
 * time, rendered beside the fields, with the register's current value shown next
 * to each proposal — because the reviewer is deciding a change, not confirming
 * a guess.
 */

/** Only obligations that actually take a certificate can be filed against. */
function documentTypeOptions(reference: ReferenceData | undefined): SearchOption[] {
  return (reference?.complianceTypes ?? [])
    .filter((t) => t.acceptsDocument)
    .map((t) => ({ value: t.complianceTypeId!, label: t.displayName ?? '' }))
}

const IntakeStatusValue = { Pending: 1, Accepted: 2, Rejected: 3, Unmatched: 4 } as const

function confidenceBand(confidence: number | null | undefined) {
  if (confidence == null) return { label: 'not read', tone: 'unk' }
  if (confidence >= 0.7) return { label: 'read well', tone: 'ok' }
  if (confidence >= 0.4) return { label: 'partly read', tone: 'warn' }
  return { label: 'barely read', tone: 'crit' }
}

/** Day-first, as every other date on this site is. */
function formatDate(iso: string | null | undefined) {
  if (!iso) return null
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${d}-${m}-${y}`
}

function formatSize(bytes: number | null | undefined) {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function IntakeQueue() {
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const status = useQuery({ queryKey: ['intake', 'status'], queryFn: api.getIntakeStatus })
  const reference = useQuery({ queryKey: ['reference'], queryFn: api.getReference })

  const queue = useQuery({
    queryKey: ['intake', 'queue'],
    queryFn: () => api.getIntakeQueue(1, 100),
  })

  const items = useMemo(() => queue.data?.items ?? [], [queue.data])

  // Select the first item, and re-select when the current one has been dealt
  // with. Without this the reviewer accepts a certificate and lands on an empty
  // pane, having to click back into the list for every single one.
  useEffect(() => {
    if (items.length === 0) {
      setSelectedId(null)
      return
    }
    if (!items.some((i) => i.documentIntakeId === selectedId)) {
      setSelectedId(items[0].documentIntakeId ?? null)
    }
  }, [items, selectedId])

  const selected = items.find((i) => i.documentIntakeId === selectedId) ?? null

  return (
    <main className="intake">
      <header className="intake__head">
        <div>
          <p className="eyebrow">Email intake</p>
          <h1 className="intake__title">Certificates awaiting review</h1>
        </div>

        <Link className="intake__back" to="/register">
          Back to vehicles
        </Link>
      </header>

      {/*
        Said plainly rather than shown as an empty list. An empty queue and an
        unconfigured mailbox look identical on screen, and telling somebody who
        is waiting on a renewal that there is nothing to review — when nothing is
        being read at all — is the wrong answer.
      */}
      {status.data && !status.data.isConfigured && (
        <p className="intake__note">
          {status.data.message} Certificates can still be uploaded by hand from any
          vehicle&rsquo;s page.
        </p>
      )}

      {queue.isLoading && <p className="intake__note">Loading the queue…</p>}

      {queue.isError && (
        <p className="intake__note intake__note--bad">
          The queue could not be loaded. {(queue.error as ApiError)?.message}
        </p>
      )}

      {!queue.isLoading && items.length === 0 && status.data?.isConfigured && (
        <p className="intake__note">
          Nothing is waiting. Anything arriving in the watched mailbox appears here
          for review.
        </p>
      )}

      {items.length > 0 && (
        <div className="intake__split">
          <ol className="intake__list">
            {items.map((item) => {
              const band = confidenceBand(item.confidence)
              const unmatched =
                item.status === IntakeStatusValue.Unmatched || !item.matchedVehicleId

              return (
                <li key={item.documentIntakeId}>
                  <button
                    type="button"
                    className={`intake__row${
                      item.documentIntakeId === selectedId ? ' intake__row--on' : ''
                    }`}
                    onClick={() => setSelectedId(item.documentIntakeId ?? null)}
                  >
                    <span className="intake__rowtop">
                      <span className="intake__reg mono">
                        {item.matchedRegistration ??
                          item.extractedRegistration ??
                          'no plate found'}
                      </span>
                      <span className={`intake__band intake__band--${band.tone}`}>
                        {band.label}
                      </span>
                    </span>

                    <span className="intake__rowsub">
                      {item.complianceTypeName ?? 'obligation unknown'}
                      {item.extractedExpiryDate
                        ? ` · ${formatDate(item.extractedExpiryDate)}`
                        : ' · no date read'}
                    </span>

                    {unmatched && <span className="intake__flag">Needs a vehicle</span>}
                  </button>
                </li>
              )
            })}
          </ol>

          {/* Keyed, so every field resets when the reviewer moves on. Without the
              key, edits typed against one certificate would still be sitting in
              the form when the next one loads. */}
          {selected && (
            <ReviewPane
              key={selected.documentIntakeId}
              item={selected}
              typeOptions={documentTypeOptions(reference.data)}
            />
          )}
        </div>
      )}
    </main>
  )
}

function ReviewPane({ item, typeOptions }: { item: IntakeItem; typeOptions: SearchOption[] }) {
  const queryClient = useQueryClient()

  const [complianceTypeId, setComplianceTypeId] = useState<number | ''>(
    item.complianceTypeId ?? '',
  )
  const [expiryDate, setExpiryDate] = useState(item.extractedExpiryDate?.slice(0, 10) ?? '')
  const [certificateNumber, setCertificateNumber] = useState(
    item.extractedCertificateNumber ?? '',
  )
  const [providerName, setProviderName] = useState(item.extractedProviderName ?? '')
  const [notes, setNotes] = useState('')
  const [applyToRegister, setApplyToRegister] = useState(true)
  const [rejectReason, setRejectReason] = useState('')
  const [showReject, setShowReject] = useState(false)

  // Unmatched arrivals: the reviewer names the vehicle. A typed registration is
  // resolved against the register rather than accepted as text — a certificate
  // filed against a vehicle that does not exist is not a recoverable mistake,
  // it is a document nobody will ever find again.
  const [vehicleId, setVehicleId] = useState<number | null>(item.matchedVehicleId ?? null)
  const [lookup, setLookup] = useState(item.extractedRegistration ?? '')
  const [lookupState, setLookupState] = useState<string | null>(null)

  const [scanUrl, setScanUrl] = useState<string | null>(null)
  const [scanError, setScanError] = useState<string | null>(null)

  useEffect(() => {
    let abandoned = false
    let url: string | null = null

    api
      .fetchIntakeBlobUrl(item.documentIntakeId!)
      .then((u) => {
        // The reviewer may have moved on while this was in flight. Releasing it
        // straight away is what stops the blob being held for the life of the page.
        if (abandoned) {
          URL.revokeObjectURL(u)
          return
        }
        url = u
        setScanUrl(u)
      })
      .catch((e: ApiError) => setScanError(e.message))

    return () => {
      abandoned = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [item.documentIntakeId])

  const resolveVehicle = async () => {
    const typed = lookup.trim()
    if (!typed) return

    setLookupState('Looking…')
    try {
      const found = await api.findByRegistration(typed)
      if (found) {
        setVehicleId(found.vehicleId ?? null)
        setLookupState(`Matched ${found.registration ?? typed}`)
      } else {
        setVehicleId(null)
        setLookupState('No vehicle with that registration is in the register.')
      }
    } catch {
      setLookupState('That lookup failed.')
    }
  }

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['intake'] })
    invalidateFleet(queryClient)
  }

  const accept = useMutation({
    mutationFn: () =>
      api.acceptIntake(item.documentIntakeId!, {
        vehicleId: vehicleId!,
        complianceTypeId: complianceTypeId as number,
        expiryDate: expiryDate || null,
        certificateNumber: certificateNumber.trim() || null,
        providerName: providerName.trim() || null,
        applyExpiryToRegister: applyToRegister,
        notes: notes.trim() || null,
        rowVersion: item.rowVersion!,
      }),
    onSuccess: invalidate,
  })

  const reject = useMutation({
    mutationFn: () =>
      api.rejectIntake(item.documentIntakeId!, rejectReason.trim(), item.rowVersion!),
    onSuccess: invalidate,
  })

  const band = confidenceBand(item.confidence)
  const canAccept = vehicleId != null && complianceTypeId !== '' && !accept.isPending

  const currentText = item.currentExpiryDate
    ? formatDate(item.currentExpiryDate)
    : 'nothing on record'
  const changes = applyToRegister && !!expiryDate && item.currentExpiryDate !== expiryDate

  return (
    <section className="intake__pane">
      <header className="intake__panehead">
        <h2 className="intake__filename">{item.fileName}</h2>
        <p className="intake__meta">
          {item.from ?? 'unknown sender'} · {formatSize(item.sizeBytes)} ·{' '}
          <span className={`intake__band intake__band--${band.tone}`}>{band.label}</span>
        </p>
        {item.subject && <p className="intake__subject">{item.subject}</p>}
      </header>

      {item.extractionError && (
        <p className="intake__note intake__note--warn">{item.extractionError}</p>
      )}

      <div className="intake__body">
        {/* The scan itself, at the size of the pane. Reading the certificate is
            the actual work here; the fields beside it are only the shorthand. */}
        <div className="intake__scan">
          {scanError && <p className="intake__note intake__note--bad">{scanError}</p>}
          {!scanUrl && !scanError && <p className="intake__note">Opening the scan…</p>}
          {scanUrl &&
            (item.contentType?.startsWith('image/') ? (
              <img src={scanUrl} alt={`Scan of ${item.fileName}`} />
            ) : (
              <iframe src={scanUrl} title={`Scan of ${item.fileName}`} />
            ))}
        </div>

        <form
          className="intake__form"
          onSubmit={(e) => {
            e.preventDefault()
            if (canAccept) accept.mutate()
          }}
        >
          <div className="intake__field">
            <label className="eyebrow" htmlFor="intake-vehicle">
              Vehicle
            </label>

            {item.matchedVehicleId && vehicleId === item.matchedVehicleId ? (
              <p className="intake__matched">
                <span className="mono">{item.matchedRegistration}</span>
                <span className="intake__matchedsub">
                  {[item.matchedVehicleType, item.matchedSite].filter(Boolean).join(' · ')}
                </span>
              </p>
            ) : (
              <>
                <div className="intake__lookup">
                  <input
                    id="intake-vehicle"
                    className="intake__input mono"
                    value={lookup}
                    onChange={(e) => setLookup(e.target.value.toUpperCase())}
                    placeholder="MH12EQ3823"
                  />
                  <button type="button" className="intake__btn" onClick={resolveVehicle}>
                    Find
                  </button>
                </div>
                {lookupState && <p className="intake__hint">{lookupState}</p>}
              </>
            )}
          </div>

          <div className="intake__field">
            <label className="eyebrow" htmlFor="intake-type">
              Obligation
            </label>
            <SearchSelect
              id="intake-type"
              options={typeOptions}
              value={complianceTypeId}
              onChange={(v) => setComplianceTypeId(v)}
              placeholder="Search obligations…"
            />
          </div>

          <div className="intake__field">
            <label className="eyebrow" htmlFor="intake-expiry">
              Expiry printed on the certificate
            </label>
            <input
              id="intake-expiry"
              type="date"
              className="intake__input"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
            />
            <p className="intake__hint">Register today: {currentText}</p>
          </div>

          <div className="intake__pair">
            <div className="intake__field">
              <label className="eyebrow" htmlFor="intake-cert">
                Certificate number
              </label>
              <input
                id="intake-cert"
                className="intake__input mono"
                value={certificateNumber}
                onChange={(e) => setCertificateNumber(e.target.value)}
              />
            </div>

            <div className="intake__field">
              <label className="eyebrow" htmlFor="intake-provider">
                Issued by
              </label>
              <input
                id="intake-provider"
                className="intake__input"
                value={providerName}
                onChange={(e) => setProviderName(e.target.value)}
              />
            </div>
          </div>

          <label className="intake__check">
            <input
              type="checkbox"
              checked={applyToRegister}
              onChange={(e) => setApplyToRegister(e.target.checked)}
            />
            <span>
              Correct the register to this date
              <span className="intake__hint">
                Files as a manual override, so a later registry sync reports it as
                kept rather than overwriting it.
              </span>
            </span>
          </label>

          {/*
            The one case worth interrupting for. Somebody has already asserted
            this date by hand; overwriting it is allowed — a person is reviewing,
            which is exactly what a registry sync is not — but it must not happen
            silently.
          */}
          {changes && item.currentIsManualOverride && (
            <p className="intake__note intake__note--warn">
              The register&rsquo;s current date was set by hand. Accepting replaces it
              with {formatDate(expiryDate)}.
            </p>
          )}

          {changes && !item.currentIsManualOverride && (
            <p className="intake__note">
              The register will change from {currentText} to {formatDate(expiryDate)}.
            </p>
          )}

          <div className="intake__field">
            <label className="eyebrow" htmlFor="intake-notes">
              Notes
            </label>
            <input
              id="intake-notes"
              className="intake__input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional"
            />
          </div>

          {accept.isError && (
            <p className="intake__note intake__note--bad">
              {(accept.error as ApiError).message}
            </p>
          )}

          <div className="intake__actions">
            <button type="submit" className="intake__btn intake__btn--go" disabled={!canAccept}>
              {accept.isPending ? 'Filing…' : 'Accept and file'}
            </button>

            <button
              type="button"
              className="intake__btn"
              onClick={() => setShowReject((v) => !v)}
            >
              Reject
            </button>
          </div>

          {showReject && (
            <div className="intake__reject">
              <label className="eyebrow" htmlFor="intake-reason">
                Why is this being dismissed?
              </label>
              <input
                id="intake-reason"
                className="intake__input"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="An advert, a duplicate, the wrong document…"
              />
              <p className="intake__hint">
                The file and this record are kept. This is the record of what arrived
                and what somebody decided about it.
              </p>
              {reject.isError && (
                <p className="intake__note intake__note--bad">
                  {(reject.error as ApiError).message}
                </p>
              )}
              <button
                type="button"
                className="intake__btn intake__btn--bad"
                disabled={!rejectReason.trim() || reject.isPending}
                onClick={() => reject.mutate()}
              >
                {reject.isPending ? 'Dismissing…' : 'Dismiss this arrival'}
              </button>
            </div>
          )}
        </form>
      </div>
    </section>
  )
}
